import type { ExportStatus } from '@gozoki/api-types'
import { isExportInFlight } from './export-jobs.ts'

/**
 * Exports suivis par l'onglet (lot S8.4) : ceux que l'admin a lancés depuis le chargement de la
 * page, hors de tout écran, pour que le toast et le compteur de la cloche les suivent jusqu'au bout
 * quand il change d'écran. En mémoire seulement : un rechargement (dont tout changement d'identité,
 * `utils/session-navigation.ts`) repart de zéro, et l'écran « Exports » (S8.5) garde l'historique.
 */

export interface TrackedExport {
  /** `public_id`. */
  id: string
  /** Libellé du bouton qui l'a lancé, repris par les toasts. */
  label: string
  /** Début du suivi (ms), qui règle le rythme du sondage. */
  trackedAt: number
  /**
   * Statut final annoncé (sondage, évènement ou suivi refusé), `null` tant que le suivi continue.
   * Le statut en cours vient du cache React Query de `GET /exports/:id`, pas d'ici.
   */
  settledStatus: ExportStatus | null
}

export interface ExportTracker {
  /** Suit un export ; un export déjà suivi et pas encore terminé (double clic) garde son suivi. */
  track: (entry: Pick<TrackedExport, 'id' | 'label'>) => TrackedExport
  /**
   * Statut final : rend l'export la première fois seulement, `null` ensuite (sondage et évènement
   * peuvent l'annoncer tous deux), pour un seul téléchargement et un seul toast.
   */
  settle: (id: string, status: ExportStatus) => TrackedExport | null
  get: (id: string) => TrackedExport | undefined
  subscribe: (listener: () => void) => () => void
  /** Liste stable entre deux changements (`useSyncExternalStore`). */
  getSnapshot: () => readonly TrackedExport[]
}

export const createExportTracker = (now: () => number = Date.now): ExportTracker => {
  const entries = new Map<string, TrackedExport>()
  const listeners = new Set<() => void>()
  let snapshot: readonly TrackedExport[] = []

  const write = (entry: TrackedExport) => {
    entries.set(entry.id, entry)
    snapshot = [...entries.values()]
    for (const listener of listeners) listener()
  }

  return {
    track: ({ id, label }) => {
      const current = entries.get(id)
      if (current && current.settledStatus === null) return current
      const entry = { id, label, trackedAt: now(), settledStatus: null }
      write(entry)
      return entry
    },
    settle: (id, status) => {
      const current = entries.get(id)
      if (!current || current.settledStatus !== null || isExportInFlight(status)) return null
      const settled = { ...current, settledStatus: status }
      write(settled)
      return settled
    },
    get: (id) => entries.get(id),
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    getSnapshot: () => snapshot,
  }
}

/** Exports encore en file ou en cours : ceux que l'écouteur sonde et que la cloche compte. */
export const inFlightExports = (entries: readonly TrackedExport[]): TrackedExport[] =>
  entries.filter((entry) => entry.settledStatus === null)

/** Suivi de l'onglet, partagé par `useExportJob`, l'écouteur du layout et la cloche. */
export const exportTracker = createExportTracker()
